import { useState, useRef } from 'react';
import SkyPlanner from './SkyPlanner.jsx';

// More pane, the overflow surface from the mobile IA spec (§1B): the long
// tail that doesn't fit the primary tabs. Carries:
//   • Sky Planner, the desktop planning overlay, launched full-screen
//   • Visible now, satellites overhead RIGHT NOW (parity with LeftConsole's
//     visibleNow list), doubling as the PARALLEL A11Y LIST (spec §5): a real
//     role="listbox" that drives the same selection state as tapping the
//     canvas, arrow-key navigable, so the globe is no longer AT-opaque.

const MONO = 'var(--mono)';

const SECTION_LABEL = {
  color: 'var(--txt-dim)', fontSize: 'var(--ui-label)', letterSpacing: '0.18em',
  fontFamily: MONO, fontWeight: 700, textTransform: 'uppercase',
  marginBottom: 8, marginTop: 14,
};

const BIG_BTN = {
  display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 48,
  padding: '10px 12px', marginBottom: 6, textAlign: 'left',
  background: 'var(--bg-2)', border: '1px solid var(--hair-2)', borderRadius: 4,
  color: 'var(--txt)', fontFamily: MONO, fontSize: 'var(--ui-body)', cursor: 'pointer',
};

export default function MobilePaneMore({
  observer, visibleNow = [], onSelectObject, onGoto, selectedNorad = null,
}) {
  const [plannerOpen, setPlannerOpen] = useState(false);
  const listRef = useRef(null);

  // Roving arrow-key navigation across the listbox options.
  const onListKeyDown = (e) => {
    if (!['ArrowDown', 'ArrowUp'].includes(e.key)) return;
    e.preventDefault();
    const opts = [...(listRef.current?.querySelectorAll('[role=option]') || [])];
    const i = opts.indexOf(document.activeElement);
    const next = e.key === 'ArrowDown' ? Math.min(i + 1, opts.length - 1) : Math.max(i - 1, 0);
    opts[next]?.focus();
  };

  return (
    <div>
      <div style={SECTION_LABEL} role="heading" aria-level="2">Planning</div>
      <a href="/observatory-office/" style={{display:"inline-block",minHeight:44,padding:"12px 0",color:"var(--txt-dim)"}}>Observatory office</a>
      <button style={BIG_BTN} onClick={() => setPlannerOpen(true)}>
        <span aria-hidden="true" style={{ fontSize: 16 }}>✦</span>
        <span style={{ flex: 1 }}>
          <span style={{ display: 'block', fontWeight: 700 }}>Sky Planner</span>
          <span style={{ display: 'block', fontSize: 'var(--ui-label)', color: 'var(--txt-dim)' }}>
            Satellites · planets · deep-sky, ranked for your site
          </span>
        </span>
        <span aria-hidden="true" style={{ color: 'var(--txt-dim)' }}>›</span>
      </button>

      <div style={SECTION_LABEL} role="heading" aria-level="2">
        Above horizon · sunlit · {visibleNow.length}
      </div>
      <p className="og-layer-help">Horizon position and sunlight are not an observing recommendation. Darkness, brightness, clouds and your sightline still matter. Use Sky Planner for rated passes.</p>
      {!observer && (
        <div style={{ color: 'var(--txt-dim)', fontFamily: MONO, fontSize: 'var(--ui-label)', padding: '4px 0 10px' }}>
          Set a location in the Tonight tab to see what&#39;s overhead.
        </div>
      )}
      {observer && visibleNow.length === 0 && (
        <div style={{ color: 'var(--txt-dim)', fontFamily: MONO, fontSize: 'var(--ui-label)', padding: '4px 0 10px' }}>
          No sunlit catalog objects above your horizon in this list.
        </div>
      )}
      {visibleNow.length > 0 && (
        <div
          ref={listRef}
          role="listbox"
          aria-label="Sunlit satellites above the horizon, selecting one opens its detail card"
          onKeyDown={onListKeyDown}
          style={{ marginBottom: 10 }}
        >
          {visibleNow.map((s, i) => {
            const isSel = selectedNorad != null && s.norad_id === selectedNorad;
            return (
              <div
                key={s.norad_id ?? i}
                role="option"
                aria-selected={isSel}
                tabIndex={i === 0 ? 0 : -1}
                aria-label={`${s.name || 'Satellite ' + s.norad_id}, ${s.category || 'satellite'}${s.observer?.elevation != null ? `, elevation ${Math.round(s.observer.elevation)} degrees` : ''}${s.observer?.azimuth != null ? `, azimuth ${Math.round(s.observer.azimuth)} degrees` : ''}, above horizon and sunlit`}
                onClick={() => onSelectObject?.(s)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectObject?.(s); } }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, minHeight: 48,
                  padding: '8px 12px', marginBottom: 4,
                  background: isSel ? 'var(--amber-2)' : 'var(--bg-2)',
                  border: `1px solid ${isSel ? 'var(--accent)' : 'var(--hair)'}`,
                  borderRadius: 4, cursor: 'pointer',
                }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontFamily: MONO, fontSize: 'var(--ui-body)', fontWeight: 700,
                                 color: 'var(--txt)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.name || `NORAD ${s.norad_id}`}
                  </span>
                  <span style={{ display: 'block', fontFamily: MONO, fontSize: 'var(--ui-label)', color: 'var(--txt-dim)' }}>
                    {s.category || 'satellite'}
                    {s.observer?.elevation != null ? ` · el ${Math.round(s.observer.elevation)}°` : ''}
                    {s.observer?.azimuth != null ? ` · az ${Math.round(s.observer.azimuth)}°` : ''}
                  </span>
                </span>
                <span aria-hidden="true" style={{ color: 'var(--txt-dim)' }}>›</span>
              </div>
            );
          })}
        </div>
      )}

      {plannerOpen && (
        <SkyPlanner observer={observer} open={plannerOpen}
                    onClose={() => setPlannerOpen(false)} onGoto={onGoto} />
      )}
    </div>
  );
}
